// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import { fmtDate } from '../utils/timezoneUtils';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ContentSkeleton from './ContentSkeleton';
import { createCancelableRemoteGet } from '../utils/remoteApiCancelable';
import axios from 'axios';
import { retryAsync } from '../utils/retry';
import { useTheme } from '../context/ThemeContext';
import { getGlobalBackoff } from '../utils/exponentialBackoff';

const getImageNames = (image) => {
  const tags = image.RepoTags || [];
  const names = image.Names || [];
  const all = [...new Set([...tags, ...names])].filter(
    (n) => n && n !== '<none>:<none>' && n !== '<none>'
  );
  return all;
};

const deduplicateImages = (images) => {
  const byId = new Map();
  for (const img of images) {
    const id = img.Id || img.id || '';
    if (byId.has(id)) {
      const existing = byId.get(id);
      const merged = [...getImageNames(existing), ...getImageNames(img)];
      existing._allNames = [...new Set(merged)].sort();
    } else {
      const clone = { ...img, _allNames: getImageNames(img).sort() };
      byId.set(id, clone);
    }
  }
  // Stable order: the Docker API does not guarantee ordering between polls,
  // and a reshuffle every 5s makes the cards jump around on screen.
  return Array.from(byId.values()).sort((a, b) =>
    (a._allNames[0] || a.Id || '').localeCompare(b._allNames[0] || b.Id || '')
  );
};

/** Stable container ordering (see deduplicateImages) + drop the churn-y
 * human-relative Status text from equality checks via sameData below. */
const sortContainers = (containers) =>
  [...containers].sort((a, b) =>
    (a.Names?.[0] || a.Id || '').localeCompare(b.Names?.[0] || b.Id || '')
  );

/** Deep-equality via JSON — payloads are small; skipping no-op setState keeps
 * the card from re-rendering (and subtly reflowing) every poll tick. */
const sameData = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const StargateContainers = ({ ip, apiKey, showLoadingOverlay }) => {
  const { isDarkMode } = useTheme();
  const [containers, setContainers] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false); // For initial loading state
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false); // Track if we've loaded at least once
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const intervalRef = useRef(null);
  const activeRequestsRef = useRef([]);
  const backoff = getGlobalBackoff();

  // Base polling interval (will be adjusted by backoff on errors)
  const BASE_POLLING_INTERVAL = 5000; // 5 seconds

  useEffect(() => {
    const fetchAndPoll = async () => {
      await fetchContainers(true); // Initial load

      // Start polling with exponential backoff on errors
      const scheduleNextPoll = () => {
        if (intervalRef.current) clearTimeout(intervalRef.current);

        // Get backoff delay for containers polling
        const backoffKey = `containers-${ip}`;
        const delay = backoff.getDelay(backoffKey) || BASE_POLLING_INTERVAL;

        intervalRef.current = setTimeout(() => {
          fetchContainers(false).finally(() => {
            scheduleNextPoll(); // Schedule next poll regardless of success/failure
          });
        }, delay);
      };

      scheduleNextPoll();
    };

    fetchAndPoll();
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      // Cancel all active requests when component unmounts
      activeRequestsRef.current.forEach((req) => {
        if (req && req.cancel) {
          req.cancel();
        }
      });
      activeRequestsRef.current = [];
      // Reset backoff state for this component
      backoff.resetKey(`containers-${ip}`);
    };
  }, [ip, apiKey]);

  const fetchContainers = async (isInitial = false) => {
    if (isInitial || !hasLoadedOnce) setLoading(true);
    // Note: error is NOT cleared here. Clearing at the start of every poll and
    // re-setting on failure made the inline Alert unmount/remount each tick,
    // shifting the whole card. It is cleared on success instead.

    // Clear previous requests
    activeRequestsRef.current.forEach((req) => {
      if (req && req.cancel) {
        req.cancel();
      }
    });
    activeRequestsRef.current = [];

    try {
      const retryFetch = () =>
        retryAsync(async () => {
          // Create cancelable requests
          const containersReq = createCancelableRemoteGet(ip, apiKey, '/api/container/containers');
          activeRequestsRef.current.push(containersReq);

          const data = await containersReq.request;

          // Extract .containers if present
          return data?.containers ?? data;
        }, 3);

      const result = await retryFetch();
      const sorted = sortContainers(Array.isArray(result) ? result : []);
      setContainers((prev) => (sameData(prev, sorted) ? prev : sorted));
      setError(null);
      if (!hasLoadedOnce) setHasLoadedOnce(true);

      // Fetch images after containers (don't let errors here break the flow)
      fetchImages().catch((err) => {
        // Errors are already handled in fetchImages, just prevent unhandled rejection
        if (!axios.isCancel(err)) {
          // Silently handle - fetchImages already logs errors
        }
      });

      // Record success with backoff
      backoff.recordSuccess(`containers-${ip}`);
    } catch (e) {
      // Don't log or show error for canceled requests
      if (axios.isCancel(e)) {
        console.log('[StargateContainers] Request canceled');
        return;
      }

      // Record failure with exponential backoff
      const nextDelay = backoff.recordFailure(`containers-${ip}`, e);

      // Only show error if not throttled
      if (!backoff.shouldThrottleError()) {
        console.warn(
          `[Containers] Failed to fetch containers, next retry in ${Math.round(nextDelay / 1000)}s`
        );
        setError('Failed to fetch container information');
        setSnackbar({
          open: true,
          message: 'Failed to fetch containers',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchImages = async () => {
    try {
      const retryFetch = () =>
        retryAsync(async () => {
          // Create cancelable request
          const imagesReq = createCancelableRemoteGet(ip, apiKey, '/api/container/images');
          activeRequestsRef.current.push(imagesReq);

          const data = await imagesReq.request;

          // Extract .images if present
          return data?.images ?? data;
        }, 3);

      const result = await retryFetch();
      const deduped = deduplicateImages(Array.isArray(result) ? result : []);
      setImages((prev) => (sameData(prev, deduped) ? prev : deduped));

      // Record success with backoff
      backoff.recordSuccess(`images-${ip}`);
    } catch (e) {
      // Don't log or show error for canceled requests
      if (axios.isCancel(e)) {
        console.log('[StargateContainers] Images request canceled');
        return;
      }

      // Check if this is a transient error first
      const isTransientError = e.response && [502, 503, 504].includes(e.response.status);

      // Record failure with exponential backoff
      const nextDelay = backoff.recordFailure(`images-${ip}`, e);

      // Only log if not throttled and not a transient error
      // For transient errors, we suppress logging to reduce console noise
      if (!isTransientError && !backoff.shouldThrottleError()) {
        console.error('[StargateContainers] Error fetching images:', e);
      } else if (isTransientError && !backoff.shouldThrottleError()) {
        // For transient errors, only log in development mode or after multiple failures
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `[StargateContainers] Transient error fetching images (${e.response?.status || 'network'}), will retry`
          );
        }
      }

      // Don't set error state for transient errors - they'll be retried
      if (!isTransientError) {
        setError('Failed to fetch image information');
      }
    }
  };

  const getStatusColor = (status) => {
    if (status?.toLowerCase().includes('running')) return 'success';
    if (status?.toLowerCase().includes('exited')) return 'error';
    return 'default';
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: 20 }}>
            Container Management
          </Typography>
        }
        action={
          <Button
            onClick={() => fetchContainers()}
            size="small"
            sx={{
              minWidth: 0,
              color: '#fff',
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 2,
              '&:hover': { background: 'rgba(255,255,255,0.18)' },
            }}
            disabled={loading}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <svg
                style={{ width: 22, height: 22 }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0114.13-3.36L23 10M1 14l5.36 5.36A9 9 0 0020.49 15"></path>
              </svg>
            </span>
          </Button>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
          mb: 2,
        }}
      />
      <CardContent sx={{ position: 'relative' }}>
        {/* Skeleton loading state */}
        {loading && !hasLoadedOnce && <ContentSkeleton variant="list" rows={4} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Running Containers */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Running Containers ({containers.filter((c) => c.State === 'running').length}/
          {containers.length})
        </Typography>
        <List
          sx={{
            bgcolor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 3,
          }}
        >
          {containers.map((container, index) => (
            <ListItem
              key={container.Id || index}
              sx={{
                borderBottom:
                  index < containers.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                py: 2,
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {container.Names?.[0]?.replace('/', '') || container.Id?.substring(0, 12)}
                    </Typography>
                    <Chip
                      label={container.State}
                      color={getStatusColor(container.State)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ color: 'primary.light' }}>
                      Image: {container.Image} • ID: {container.Id?.substring(0, 12)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Status: {container.Status}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
          {containers.length === 0 && !loading && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    No containers found
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>

        {/* Container Images — compact row-per-image table in a FIXED-height
            scroll area (the old card grid grew unboundedly with every kept
            tag and dominated the page). Newest first. */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Container Images ({images.length})
        </Typography>
        <TableContainer
          sx={{
            maxHeight: 320,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: isDarkMode ? 'rgba(138, 148, 168, 0.04)' : '#ffffff',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Image', 'ID', 'Size', 'Created'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      bgcolor: isDarkMode ? '#141627' : '#f5f7fb',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...images]
                .sort((a, b) => (b.Created || 0) - (a.Created || 0))
                .map((image, index) => {
                  const names = image._allNames || getImageNames(image);
                  const primaryName = names[0] || 'Untagged';
                  const extraNames = names.slice(1);
                  return (
                    <TableRow key={image.Id || index} hover>
                      <TableCell sx={{ maxWidth: 380 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.light',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {primaryName}
                        </Typography>
                        {extraNames.map((name) => (
                          <Typography
                            key={name}
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#7ba8d4',
                              fontSize: '0.7rem',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {name}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        {image.Id?.replace('sha256:', '').substring(0, 12)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        {(image.Size / 1024 / 1024).toFixed(1)} MB
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        {fmtDate(image.Created * 1000)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default StargateContainers;
